// Offline Storage Utility for Destiny Junior PWA
// Uses IndexedDB for storing offline data

const DB_NAME = 'DestinyJuniorDB';
const DB_VERSION = 1;

// Store names
const STORES = {
  PENDING_ACTIVITIES: 'pending-activities',
  PENDING_DAILY_LOGS: 'pending-daily-logs',
  PENDING_NUTRITION: 'pending-nutrition',
  CACHED_CHILDREN: 'cached-children',
  CACHED_ACTIVITIES: 'cached-activities',
  OFFLINE_QUEUE: 'offline-queue'
};

let db = null;

// Initialize IndexedDB
export async function initOfflineDB() {
  return new Promise((resolve, reject) => {
    if (db) {
      resolve(db);
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      console.error('[OfflineDB] Failed to open database');
      reject(request.error);
    };

    request.onsuccess = () => {
      db = request.result;
      console.log('[OfflineDB] Database opened successfully');
      resolve(db);
    };

    request.onupgradeneeded = (event) => {
      const database = event.target.result;

      // Create stores for each data type
      Object.values(STORES).forEach(storeName => {
        if (!database.objectStoreNames.contains(storeName)) {
          database.createObjectStore(storeName, { keyPath: 'id' });
        }
      });

      console.log('[OfflineDB] Database upgraded');
    };
  });
}

// Generic save function
export async function saveToOfflineStore(storeName, data) {
  const database = await initOfflineDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);

    // Add timestamp for sync ordering
    const dataWithTimestamp = {
      ...data,
      offlineCreatedAt: new Date().toISOString()
    };

    const request = store.put(dataWithTimestamp);

    request.onsuccess = () => {
      console.log(`[OfflineDB] Saved to ${storeName}:`, data.id);
      resolve(data);
    };

    request.onerror = () => {
      console.error(`[OfflineDB] Failed to save to ${storeName}:`, request.error);
      reject(request.error);
    };
  });
}

// Generic get all function
export async function getAllFromOfflineStore(storeName) {
  const database = await initOfflineDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(storeName, 'readonly');
    const store = transaction.objectStore(storeName);
    const request = store.getAll();

    request.onsuccess = () => {
      resolve(request.result || []);
    };

    request.onerror = () => {
      console.error(`[OfflineDB] Failed to get from ${storeName}:`, request.error);
      reject(request.error);
    };
  });
}

// Generic delete function
export async function deleteFromOfflineStore(storeName, id) {
  const database = await initOfflineDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    const request = store.delete(id);

    request.onsuccess = () => {
      console.log(`[OfflineDB] Deleted from ${storeName}:`, id);
      resolve(true);
    };

    request.onerror = () => {
      console.error(`[OfflineDB] Failed to delete from ${storeName}:`, request.error);
      reject(request.error);
    };
  });
}

// Clear all data from a store
export async function clearOfflineStore(storeName) {
  const database = await initOfflineDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    const request = store.clear();

    request.onsuccess = () => {
      console.log(`[OfflineDB] Cleared ${storeName}`);
      resolve(true);
    };

    request.onerror = () => {
      console.error(`[OfflineDB] Failed to clear ${storeName}:`, request.error);
      reject(request.error);
    };
  });
}

// Save pending activity (for offline creation)
export function savePendingActivity(activity) {
  return saveToOfflineStore(STORES.PENDING_ACTIVITIES, activity);
}

// Save pending daily log
export function savePendingDailyLog(dailyLog) {
  return saveToOfflineStore(STORES.PENDING_DAILY_LOGS, dailyLog);
}

// Save pending nutrition
export function savePendingNutrition(nutrition) {
  return saveToOfflineStore(STORES.PENDING_NUTRITION, nutrition);
}

// Get all pending activities
export function getPendingActivities() {
  return getAllFromOfflineStore(STORES.PENDING_ACTIVITIES);
}

// Get all pending daily logs
export function getPendingDailyLogs() {
  return getAllFromOfflineStore(STORES.PENDING_DAILY_LOGS);
}

// Get all pending nutrition
export function getPendingNutrition() {
  return getAllFromOfflineStore(STORES.PENDING_NUTRITION);
}

// Delete synced activity
export function deletePendingActivity(id) {
  return deleteFromOfflineStore(STORES.PENDING_ACTIVITIES, id);
}

// Delete synced daily log
export function deletePendingDailyLog(id) {
  return deleteFromOfflineStore(STORES.PENDING_DAILY_LOGS, id);
}

// Delete synced nutrition
export function deletePendingNutrition(id) {
  return deleteFromOfflineStore(STORES.PENDING_NUTRITION, id);
}

// Cache children data for offline viewing
export function cacheChildren(children) {
  return Promise.all(children.map(child => 
    saveToOfflineStore(STORES.CACHED_CHILDREN, child)
  ));
}

// Get cached children
export function getCachedChildren() {
  return getAllFromOfflineStore(STORES.CACHED_CHILDREN);
}

// Add to offline queue (for any API call)
export async function addToOfflineQueue(endpoint, method, data) {
  const queueItem = {
    id: `queue-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    endpoint,
    method,
    data,
    createdAt: new Date().toISOString(),
    retryCount: 0
  };
  return saveToOfflineStore(STORES.OFFLINE_QUEUE, queueItem);
}

// Get offline queue
export function getOfflineQueue() {
  return getAllFromOfflineStore(STORES.OFFLINE_QUEUE);
}

// Remove from offline queue
export function removeFromOfflineQueue(id) {
  return deleteFromOfflineStore(STORES.OFFLINE_QUEUE, id);
}

// Check if online
export function isOnline() {
  return navigator.onLine;
}

// Sync offline data with server
export async function syncOfflineData(axiosInstance) {
  if (!isOnline()) {
    console.log('[OfflineDB] Cannot sync - offline');
    return { synced: false, reason: 'offline' };
  }

  const results = {
    activities: { success: 0, failed: 0 },
    dailyLogs: { success: 0, failed: 0 },
    nutrition: { success: 0, failed: 0 },
    queue: { success: 0, failed: 0 }
  };

  try {
    // Sync pending activities
    const pendingActivities = await getPendingActivities();
    for (const activity of pendingActivities) {
      try {
        await axiosInstance.post('/activities', activity);
        await deletePendingActivity(activity.id);
        results.activities.success++;
      } catch (error) {
        console.error('[OfflineDB] Failed to sync activity:', error);
        results.activities.failed++;
      }
    }

    // Sync pending daily logs
    const pendingDailyLogs = await getPendingDailyLogs();
    for (const log of pendingDailyLogs) {
      try {
        await axiosInstance.post('/daily-logs', log);
        await deletePendingDailyLog(log.id);
        results.dailyLogs.success++;
      } catch (error) {
        console.error('[OfflineDB] Failed to sync daily log:', error);
        results.dailyLogs.failed++;
      }
    }

    // Sync pending nutrition
    const pendingNutrition = await getPendingNutrition();
    for (const nutrition of pendingNutrition) {
      try {
        await axiosInstance.post('/nutrition', nutrition);
        await deletePendingNutrition(nutrition.id);
        results.nutrition.success++;
      } catch (error) {
        console.error('[OfflineDB] Failed to sync nutrition:', error);
        results.nutrition.failed++;
      }
    }

    // Process offline queue
    const queue = await getOfflineQueue();
    for (const item of queue) {
      try {
        await axiosInstance({
          method: item.method,
          url: item.endpoint,
          data: item.data
        });
        await removeFromOfflineQueue(item.id);
        results.queue.success++;
      } catch (error) {
        console.error('[OfflineDB] Failed to process queue item:', error);
        results.queue.failed++;
      }
    }

    console.log('[OfflineDB] Sync completed:', results);
    return { synced: true, results };

  } catch (error) {
    console.error('[OfflineDB] Sync error:', error);
    return { synced: false, error: error.message };
  }
}

// Request background sync (if supported)
export async function requestBackgroundSync(tag = 'sync-data') {
  if ('serviceWorker' in navigator && 'SyncManager' in window) {
    try {
      const registration = await navigator.serviceWorker.ready;
      await registration.sync.register(tag);
      console.log('[OfflineDB] Background sync registered:', tag);
      return true;
    } catch (error) {
      console.error('[OfflineDB] Background sync failed:', error);
      return false;
    }
  }
  return false;
}

// Initialize and export
export { STORES };
export default {
  initOfflineDB,
  saveToOfflineStore,
  getAllFromOfflineStore,
  deleteFromOfflineStore,
  clearOfflineStore,
  savePendingActivity,
  savePendingDailyLog,
  savePendingNutrition,
  getPendingActivities,
  getPendingDailyLogs,
  getPendingNutrition,
  deletePendingActivity,
  deletePendingDailyLog,
  deletePendingNutrition,
  cacheChildren,
  getCachedChildren,
  addToOfflineQueue,
  getOfflineQueue,
  removeFromOfflineQueue,
  isOnline,
  syncOfflineData,
  requestBackgroundSync
};
